<template>
  <AccountLayout>
    <div class="w-full lg:w-2/5">
      <div class="checkout__form">
        <div
          v-if="updatedSuccessfully"
          class="flex items-center justify-center p-2 my-6 text-center text-white rounded-md success">
          Your card details have been updated successfully.
        </div>
        <h2 class="form__title leading-none mb-6 text-[26px] font-bold">
          Billing Details
        </h2>
        <div
          v-if="errorMessage.length > 0"
          class="flex items-center justify-center p-2 my-6 text-center text-white bg-red-500 rounded-md">
          {{ errorMessage }}
        </div>
        <div v-if="isPayPalPaymentMethod" class="form__structure">
          <div class="form__title leading-none font-medium mb-6">
            Your payments are managed by PayPal.
            <!--Your PayPal account is: {{paypalEmail}}-->
            <!-- TODO: Paypal e-mail address will be implemented later. It makes too much effort to put paypalEmail here. -->
          </div>
          <a
            href="https://www.paypal.com/us/signin"
            target="_blank"
            class="al-btn al-btn--primary font-bold w-48 px-3 flex align-center justify-center">
            Go to PayPal
          </a>
        </div>
        <div v-else-if="isGooglePayPaymentMethod" class="form__structure">
          <div class="form__title leading-none font-medium mb-6">
            Your payments are managed by Google Pay.
          </div>
          <a
            href="https://pay.google.com"
            target="_blank"
            class="al-btn al-btn--primary font-bold w-48 px-3 flex align-center justify-center">
            Go to Google Pay
          </a>
        </div>
        <div v-else-if="isApplePayPaymentMethod" class="form__structure">
          <div class="form__title leading-none font-medium mb-6">
            Your payments are managed by Apple Pay.
          </div>
        </div>
        <div v-else>
          <div>
            <label for="full_name" class="form__title leading-none font-medium">Name on Card</label>
            <FullNameField
              id="full_name"
              ref="fullName"
              v-model="inputs.full_name"
              class="w-full field mb-6 mt-2"
              label="Name on card"
              :error="errors.full_name"
              :label-class="invisibleLabel ? 'invisibleLabel' : ''"
              is-dark-floating-label
              :is-pricing-form-name="true"
              @onFocus="onFocus()"
              @onBlur="onBlur('full_name')"
              />
          </div>
          <div>
            <label for="card_component" class="form__title leading-none font-medium"
              >Credit Card</label
            >
            <CreditCardField
              v-if="shouldRenderChargebeeCardComponent"
              ref="creditCard"
              class="w-full field cc-field mt-2"
              :updated-successfully="updatedSuccessfully"
              @chargebee-card-component="chargebeeCardComponent = $event"
              @card-error="cardError = $event"
              @card-valid="cardValid = $event"
               />
            <div v-else class="flex my-4">
              <div class="w-4 h-4 border-b-2 border-ma rounded-full animate-spin" />
            </div>
          </div>
          <div>
            <button
              ref="submitButton"
              :disabled="submitting"
              class="al-btn al-btn--primary font-bold w-48 px-3 flex align-center justify-center"
              :class="submitting ? 'opacity-75' : 'opacity-100'"
              @click="submit">
              <span>
                <span v-show="submitting" class="animation-spin btn-spinner">
                  <i class="icon--spinner2" />
                </span>
                Update Card
              </span>
              <div v-if="submitting" class="flex items-center justify-center ml-4">
                <div class="w-2 h-2 border-b-2 border-white rounded-full animate-spin" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </AccountLayout>
</template>

<script>
import axios from 'axios';
import CreditCardField from '@/components/Fields/credit-card-combined';
import {
  PAYMENT_METHODS,
  PAYMENT_PROVIDERS,
  SESSION_NAMES,
  CREDIT_CARD_PROVIDERS,
  PAYMENT_METHOD_TYPES,
} from '@/constants';
import { eventTracker } from '@/utils/eventTracker';
import analyticsEvents from '@/utils/analyticsEvents';
import getContext from '@/utilities/get-context';
import FullNameFieldValidation from '@/mixins/form-validation/FullNameFieldValidation.vue';
import FullNameField from '../../../components/Fields/fullname';
import AccountLayout from '../components/account-layout';

export default {
  metaInfo() {
    return {
      title: 'Account | Motion Array',
    };
  },
  components: {
    AccountLayout,
    FullNameField,
    CreditCardField,
  },
  mixins: [FullNameFieldValidation],
  data() {
    return {
      inputs: {
        full_name: '',
        card_component: '',
      },
      chargebeeCardComponent: null,
      cardError: '',
      cardValid: false,
      errors: {},
      errorMessage: '',
      updatedSuccessfully: false,
      submitting: false,
      invisibleLabel: true,
      fieldsPreviousValue: '',
    };
  },
  computed: {
    paymentMethod() {
      return this.$store.getters.paymentMethod;
    },
    paymentProvider() {
      return this.$store.getters.paymentProvider;
    },
    chargebeeInstance() {
      return this.$store.getters.chargebeeInstance;
    },
    isChargebeeLoaded() {
      return this.$store.getters.isChargebeeLoaded;
    },
    shouldRenderChargebeeCardComponent() {
      return (
        this.isChargebeeLoaded &&
        this.paymentMethod === PAYMENT_METHODS.CHARGEBEE &&
        CREDIT_CARD_PROVIDERS.includes(this.paymentProvider)
      );
    },
    // TODO: get subscription from API
    paymentProviderFromContext() {
      return getContext().__MA__.billing_engine_subscription?.payment_method?.type;
    },
    isPayPalPaymentMethod() {
      return [PAYMENT_METHOD_TYPES.PAYPAL, PAYMENT_METHOD_TYPES.PAYPAL_EXPRESS_CHECKOUT].includes(this.paymentProviderFromContext?.toLowerCase());
    },
    isGooglePayPaymentMethod() {
      return this.paymentProviderFromContext?.toLowerCase() === 'google_pay';
    },
    isApplePayPaymentMethod() {
      return this.paymentProviderFromContext?.toLowerCase() === 'apple_pay';
    },
    showFullnameField() {
      return this.isLoggedIn;
    },
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
  },
  watch: {
    'inputs.full_name': function () {
      if (this.$refs.fullName.length > 0) {
        this.validateFullName(true);
      }
    },
    cardError(newVal) {
      if (newVal.length > 0) {
        eventTracker(analyticsEvents.errorMsgPrompt, {
          error_name: 'Credit Card',
          error_txt: newVal || 'Invalid card',
          click_source: 'my account- payment method tab',
        });
      }
    },
  },
  mounted() {
    try {
      if (
        !this.isChargebeeLoaded &&
        CREDIT_CARD_PROVIDERS.includes(this.paymentProvider) &&
        this.paymentProviderFromContext === 'card'
      ) {
        this.$store.dispatch('loadChargebee');
      }
    } catch (error) {
      this.errorMessage = 'Something went wrong. Please try again later.';
    }
  },
  methods: {
    validateCreditCard() {
      return this.chargebeeCardComponent.validateCardDetails();
    },
    async submit() {
      this.trackClickSave();
      this.errorMessage = '';
      this.errors = {};
      this.updatedSuccessfully = false;
      this.validateFullName(true);
      if (!this.$refs.fullName.validate) {
        eventTracker(analyticsEvents.errorMsgPrompt, {
          error_name: 'Full name',
          error_txt: 'Please enter your full name in English',
          click_source: 'my account- payment method tab',
        });
        return;
      }
      await this.validateCreditCard();

      const args = {
        payment_method_provider: this.paymentProvider,
      };
      args.full_name = this.inputs.full_name;

      args.chargebeeInstance = this.chargebeeInstance;
      args.cardComponent = this.chargebeeCardComponent;

      if (this.cardValid && this.cardError.length < 1 && args.cardComponent) {
        eventTracker(analyticsEvents.textBoxFilled, {
          text_box_name: 'Name on Card',
          click_source: 'my account- payment method tab',
          value: this.inputs.full_name,
        });
        try {
          this.submitting = true;

          // TODO: The billing engine does not support 3D update without charging, we need to implement it.
          // const res = await this.chargebeeCardComponent.authorizeWith3ds(paymentIntent, args);
          // console.log('res', res);

            let tokenResponse;
            try {
              tokenResponse = await this.chargebeeCardComponent.tokenize();
            } catch (error) {
              this.submitting = false;
              console.log('Error occurred while try to create token', error);
              this.errorMessage = 'Something went wrong. Please try again later.';
              throw error;
          }
          if (tokenResponse) {
            try {
              await axios.put('/api/payment/payment-methods', {
                payment_method_provider: args.payment_method_provider,
                token: tokenResponse.token,
              });

              this.errorMessage = '';
              this.updatedSuccessfully = true;
              this.submitting = false;
              this.inputs.full_name = '';
              this.chargebeeCardComponent.clear();
              this.errors = {};

              eventTracker(analyticsEvents.actionCompleted, {
                action_name: 'payment detailed updated',
                click_source: 'my account- payment method tab',
                inputs_edited: 'Credit Card',
              });
              sessionStorage.setItem(SESSION_NAMES.IS_DUNNING_SNACKBAR_CLOSED, 'true');
              this.$store.dispatch('setIsDunningSnackbarClosed', true);
            } catch (error) {
              if (error.response.status >= 400) {
                this.errorMessage =
                  error.response.data.message ||
                  'Problem while adding the payment method. Try again or contact us if it still persists.';
                eventTracker(analyticsEvents.errorMsgPrompt, {
                  error_name: 'Credit Card',
                  error_txt: this.errorMessage,
                  click_source: 'my account- payment method tab',
                });
              }
            }
          }
          this.submitting = false;
        } catch (error) {
          console.error(`Error occurred while try to create token. Error: ${error}`);
          throw error;
        }
      }
    },
    trackClickSave() {
      eventTracker(analyticsEvents.buttonClicked, {
        click_source: 'my account- payment method tab',
        button_name: 'Update Card',
        is_valid: this.errors.full_name === undefined,
        error_message: this.errorMessage,
      });
    },
    onFocus() {
      eventTracker(analyticsEvents.textBoxClicked, {
        text_box_name: 'Name on Card',
        click_source: 'my account- payment method tab',
      });
      this.fieldsPreviousValue = this.inputs.full_name;
    },
    onBlur(key) {
      eventTracker(analyticsEvents.textBoxFilled, {
        previous_value: this.fieldsPreviousValue,
        input_valid: this.errors[key] === undefined,
        input_value: this.inputs.full_name,
        error_text: this.errors[key],
        section_title: 'my account- payment method tab',
        text_box_name: 'Name on Card',
      });
    },
  },
};
</script>

<style scoped>
.text-\[26px\] {
  font-size: 26px;
}
.form__title {
  @apply text-primary-offwhite;
}
.success {
  background-color: #96cd89;
}
</style>
